import { useMediaQuery } from '@mui/material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { Outlet, useNavigate, useParams } from 'react-router';
import { LeftPanelSectionTemplate } from '../../../components/AdminTemplates';
import OutletContextReliantRoute from '../../../components/OutletContextReliantRoute';
import { SideActionItem } from '../../../components/SideActionsList';
import VesselOrCrewStatusBadge from '../../../components/VesselOrCrewStatusBadge';
import { useCurrentUser } from '../../../contexts/currentUser';
import { useIotsApi } from '../../../contexts/iotsApi';
import { useLanguage } from '../../../contexts/language';
import {
  ContractSelectItem,
  createContractSelectOptions,
} from '../../../hooks/useContractSelectOptions';
import { disableNavigationGuard } from '../../../hooks/useNavigationGuard';
import useQueryErrorNotifier from '../../../hooks/useQueryErrorNotifier';
import { Role } from '../../../types/user';
import { Vessel } from '../../../types/vessel';
import { getIconCertificateStatus } from '../../../utils/certificates';
import { StatusIcon } from '../../../utils/icon';
import CrewTemplateActions from '../Crew/CrewTemplateActions';
import {
  CompleteVesselContractAssignment,
  VesselTemplateContext,
} from './types';
import VesselDeletionDialog from './VesselDeletionDialog';
import { VesselCertificateStatus } from '../../../types/core';

const VesselTemplate = () => {
  const { t } = useLanguage();
  const { vesselId: vesselIdQueryParam } = useParams();
  const navigate = useNavigate();
  const {
    getVesselById,
    getVesselCertificates,
    getContracts,
    getVesselContractAssignments,
    getVesselInsurances,
  } = useIotsApi();
  const [displayedVesselName, setDisplayedVesselName] = useState<string>('');
  const { userOperator, hasRole } = useCurrentUser();
  const isMdUp = useMediaQuery('(min-width:768px)');
  const isAdmin = hasRole(Role.ADMIN);

  const [displayingDeletionDialog, setDisplayingDeletionDialog] =
    useState(false);

  const vesselId = useMemo(() => {
    return typeof vesselIdQueryParam !== 'string' ||
      vesselIdQueryParam.toLocaleLowerCase() === 'create'
      ? null
      : vesselIdQueryParam;
  }, [vesselIdQueryParam]);

  const {
    data: vessel = null,
    isFetching: isFetchingVessel,
    error,
    refetch: refetchVessel,
  } = useQuery({
    queryKey: ['vessel', vesselId ?? ''],
    queryFn: () => getVesselById(vesselId ?? ''),
    refetchOnMount: 'always',
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    staleTime: Infinity,
    enabled: vesselId != null,
  });

  const {
    data: vesselCertificates = [],
    isFetching: isFetchingCertificates,
    error: errorCertficates,
    isLoading: isLoadingCertificates,
    refetch: refetchCertificates,
  } = useQuery({
    queryKey: ['vessel', vessel?.id ?? '', 'certificates'],
    queryFn: () => getVesselCertificates(vessel?.id ?? ''),
    refetchOnMount: 'always',
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    staleTime: Infinity,
    enabled: vessel != null,
    select: certs =>
      [...certs].sort(
        (a, b) =>
          (a.status === VesselCertificateStatus.Rejected ? 1 : 0) -
            (b.status === VesselCertificateStatus.Rejected ? 1 : 0) ||
          b.endDate.localeCompare(a.endDate),
      ),
  });

  const {
    data: vesselInsurances = [],
    isFetching: isFetchingVesselInsurances,
    isPending: areVesselInsurancesPending,
    error: errorVesselInsurances,
    refetch: refetchVesselInsurances,
  } = useQuery({
    queryKey: ['vessel', vessel?.id ?? '', 'insurances'],
    queryFn: () => getVesselInsurances(vessel?.id ?? ''),
    refetchOnMount: 'always',
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    staleTime: Infinity,
    enabled: vessel != null,
    select: insurances =>
      [...insurances].sort(
        (a, b) =>
          (a.isExpired ? 0 : 1) - (a.isExpired ? 0 : 1) ||
          b.endDate.localeCompare(a.endDate),
      ),
  });

  const {
    data: { vesselContractAssignments, contracts } = {
      vesselContractAssignments: [],
      contracts: [],
    },
    isLoading: isLoadingAssignments,
    isFetching: isFetchingAssignments,
    error: assignmentsError,
  } = useQuery({
    queryKey: ['vessel', vessel?.id ?? '', 'contracts'],
    queryFn: () =>
      Promise.all([
        getVesselContractAssignments(vessel?.id ?? ''),
        getContracts(),
      ]).then(([vesselContractAssignments, contracts]) => ({
        vesselContractAssignments,
        contracts,
      })),
    enabled: vessel != null,
  });

  useQueryErrorNotifier([
    error,
    errorCertficates,
    errorVesselInsurances,
    assignmentsError,
  ]);

  const assignments = useMemo(() => {
    const { contracts: contractSelectItems } = createContractSelectOptions(
      contracts,
      userOperator,
    );
    return vesselContractAssignments
      .map<CompleteVesselContractAssignment>(assignment => ({
        ...assignment,
        contract: contractSelectItems.find(
          ({ value }) => value === assignment.contractId,
        ) as ContractSelectItem,
      }))
      .filter(({ contract }) => contract != null);
  }, [contracts, userOperator, vesselContractAssignments]);

  const queryClient = useQueryClient();

  const contextValue = useMemo<VesselTemplateContext>(
    () => ({
      vesselId,
      vessel,
      refetchVessel: () => void refetchVessel(),
      updateContextVessel: updatedVessel =>
        queryClient.setQueryData<Vessel>(
          ['vessel', vesselId ?? ''],
          updatedVessel,
        ),
      setDisplayedVesselName,
      isFetching: isFetchingVessel,
      error,
      certificatesManagement: {
        vesselCertificates,
        isFetching: isFetchingCertificates,
        error: errorCertficates,
        refetch: () => void refetchCertificates(),
      },
      insuranceManagement: {
        insurances: vesselInsurances,
        isFetching: isFetchingVesselInsurances,
        refetch: () => void refetchVesselInsurances(),
      },
      assignmentsManagement: {
        assignments,
        isLoading: isLoadingAssignments,
      },
    }),
    [
      vesselId,
      vessel,
      vesselCertificates,
      isFetchingVessel,
      error,
      isFetchingCertificates,
      errorCertficates,
      vesselInsurances,
      isFetchingVesselInsurances,
      assignments,
      isLoadingAssignments,
      refetchVessel,
      queryClient,
      refetchCertificates,
      refetchVesselInsurances,
    ],
  );

  const sidePanelActions = useMemo<SideActionItem[]>(() => {
    return [
      {
        id: 'information',
        label: t('content.information'),
        statusIcon: vessel ? StatusIcon.Completed : StatusIcon.Pending,
        url: `/admin/vessels/${vesselId ?? 'create'}`,
        isExactUrl: true,
      },
      {
        id: 'certifications',
        label: t('content.certifications'),
        statusIcon: getIconCertificateStatus(
          !vessel || isLoadingCertificates,
          vesselCertificates,
        ),
        disabled: !vessel && t('validation.certifications-tooltip'),
        url: vesselId ? `/admin/vessels/${vesselId}/certifications` : undefined,
      },
      {
        id: 'insurance',
        label: t('content.insurance'),
        statusIcon:
          !vessel || areVesselInsurancesPending
            ? StatusIcon.Pending
            : !vesselInsurances.length
              ? StatusIcon.Error
              : StatusIcon.Completed,
        disabled: !vessel && t('validation.contract-insurance-disabled-reason'),
        url: vesselId ? `/admin/vessels/${vesselId}/insurance` : undefined,
      },
      {
        id: 'assignments',
        label: t('content.assignments'),
        statusIcon:
          assignments.length > 0 ? StatusIcon.Completed : StatusIcon.Pending,
        disabled: !vessel && t('validation.assignments-tooltip'),
        url: vesselId ? `/admin/vessels/${vesselId}/contracts` : undefined,
      },
    ];
  }, [
    t,
    vessel,
    vesselId,
    isLoadingCertificates,
    vesselCertificates,
    areVesselInsurancesPending,
    vesselInsurances.length,
    assignments.length,
  ]);

  return (
    <LeftPanelSectionTemplate
      title={
        vessel ? (
          <span className="inline-flex flex-wrap items-center gap-2 md:items-end md:gap-6">
            {vessel.name}
            <VesselOrCrewStatusBadge
              entity="vessel"
              status={vessel.status}
              small={!isMdUp}
            />
          </span>
        ) : (
          displayedVesselName.trim() || t('content.untitled')
        )
      }
      isLoading={
        isFetchingVessel ||
        isFetchingCertificates ||
        isFetchingAssignments ||
        isFetchingVesselInsurances
      }
      leftPanelActions={sidePanelActions}
      goBackLinkText={t('content.back-to-vessels')}
      goBackLinkUrl="/admin/vessels"
      actions={
        vessel && (
          <>
            <CrewTemplateActions
              isAdmin={isAdmin}
              isLoading={isFetchingVessel}
              onDeleteRequest={() => setDisplayingDeletionDialog(true)}
            />
            <VesselDeletionDialog
              vesselId={displayingDeletionDialog ? vessel.id : null}
              onClose={() => setDisplayingDeletionDialog(false)}
              onConfirm={() => {
                setDisplayingDeletionDialog(false);
                void disableNavigationGuard(() => navigate('/admin/vessels'));
              }}
            />
          </>
        )
      }
    >
      <Outlet context={contextValue} />
    </LeftPanelSectionTemplate>
  );
};

export default VesselTemplate;

export const VesselSubPageWrapper = () => (
  <OutletContextReliantRoute<VesselTemplateContext>
    getOutletContextState={({ vessel, vesselId, isFetching }) =>
      // If the vessel has been deleted, it should not be accessible
      vessel && !vessel.deleteDate
        ? 'ready'
        : vesselId && isFetching
          ? 'pending'
          : 'error'
    }
    errorRedirectionPath="/admin/vessels"
  />
);
